/**
 * Landing on the jobs list (refresh, link, bookmark), its request waited for
 * the shell (user, permissions, saved filters) and then its lazy chunk — on
 * demo2 it started ~3.5s in. Here the chunk and the last first-page request
 * start during boot, alongside the bootstrap calls.
 *
 * The imports match the `/jobs` route in `components/getRoutes` (it renders
 * one or the other depending on a flag); the query key and fetcher match the
 * page's `useJobsListWithParams`, so the page reads this entry instead of
 * fetching again.
 */
import { queryClient } from '@/lib/queryClient';
import { jobsApi } from '@/api/jobs/jobs.api';
import { jobsKeys } from '@/api/jobs/jobs.keys';
import { readLastJobsListRequest } from '@/pages/ats-jobs/api/lastJobsListRequest';

let requested = false;
let chunksRequested = false;

export const isJobsListPath = (pathname: string): boolean =>
  pathname === '/jobs' || pathname === '/jobs/';

const preloadChunks = () => {
  if (chunksRequested) return;
  chunksRequested = true;
  const retry = () => {
    // A failed preload must not surface — React.lazy retries on navigation.
    requested = false;
    chunksRequested = false;
  };
  import('@/pages/ats-jobs').catch(retry);
  import('@/pages/ats-jobs/JobsWithTabs').catch(retry);
};

const hasSession = (): boolean => {
  try {
    return Boolean(localStorage.getItem('apiv1Auth'));
  } catch {
    return false;
  }
};

/**
 * Starts the page's last first-page request. `prefetchQuery` is a no-op while
 * that entry is fresh, so warming twice — or after a visit — costs nothing.
 */
const prefetchLastJobsList = (): void => {
  // Without a session the call would only bounce off auth.
  if (!hasSession()) return;

  const params = readLastJobsListRequest();
  if (!params) return;

  void queryClient.prefetchQuery({
    queryKey: jobsKeys.list(params),
    queryFn: () => jobsApi.list(params),
    // Same as the page's query, so its mount reads this rather than refetching.
    staleTime: 30_000,
  });
};

export const preloadJobsListRoute = (
  location: Pick<Location, 'pathname' | 'search'> = window.location
): void => {
  if (requested || !isJobsListPath(location.pathname)) return;
  requested = true;

  preloadChunks();

  // A searched entry builds a different request.
  if (new URLSearchParams(location.search).has('search')) return;
  prefetchLastJobsList();
};

/**
 * Loads the jobs list's code once the app has settled, so the first in-app
 * visit doesn't wait for it. Code only — no request.
 */
export const preloadJobsListChunks = (): void => preloadChunks();

/**
 * The jobs list is about to be opened from inside the app (the pointer or
 * focus is on a link to it): start its code and its request now, so both are
 * under way — often done — by the time the click lands. On demo2 the request
 * alone took ~1.3s and only started ~0.7s after the click.
 */
export const warmJobsListRoute = (): void => {
  preloadChunks();
  prefetchLastJobsList();
};
